/* everaw-video.js -- one adaptive-playback path for every background video.
   v21, 2026-08-26. REWRITTEN after adversarial review; the notes below record
   what the review falsified, because each one is a trap for the next editor.

   THE ORIGINAL BUG (unchanged story).
   Sections gated adaptive streaming on canPlayType('application/vnd.apple.mpegurl'),
   which is truthy on Safari/iOS and EMPTY on Chrome and Android WebView, so the
   adaptive path was iPhone-only. Android is 49.1% of our orders and the homepage
   carries 75.3 MB of mp4.

   WHAT THE REVIEW FALSIFIED IN v1 OF THIS FILE:

   1. capLevelToPlayerSize alone does NOT pick the small rendition. hls.js
      multiplies the CSS box by devicePixelRatio unless ignoreDevicePixelRatio is
      set, and picks the first level >= max(w,h). A 250px box at DPR 2-3 asked
      for 888-1332px and got the TOP rung on every phone we ship to. With
      ignoreDevicePixelRatio:true the same box asks for ~444px and gets 270x480,
      which is the whole point. For decorative loops the DPR sharpness is not
      worth 3-4x the bytes.

   2. One Hls instance per intersecting video leaks. Splide clones the hero
      slide (up to 5 nodes), loop-mode translates clones INTO view, and nothing
      ever destroyed an instance: up to 14 live workers + full-clip
      SourceBuffers on one homepage, inside the Instagram webview. Fix: a
      module-level registry caps live instances (MAX_HLS); the oldest is
      destroyed and its element handed the plain mp4, which by then is usually
      cached anyway.

   3. The idempotence guard lived in dataset, and Hls writes a blob: URL into
      video.src -- both survive cloneNode(true), so a Splide refresh could clone
      a "started" element with a dead MediaSource URL and the guard would skip
      re-attaching. State now lives in WeakSets keyed on the element identity,
      which clones never inherit, and begin()/attach() strip a stale blob: src.

   4. The native-HLS rung had no error path: set .src to the manifest and hope.
      A 404'd manifest left a poster forever. There is now an error listener
      that falls back to the mp4 <source> (restored by removing the src
      attribute and calling load()).

   5. Fatal MSE errors destroyed the player outright. hls.js's own documented
      pattern is recoverMediaError() for MEDIA_ERROR and startLoad() for
      NETWORK_ERROR -- both routine on Android WebView -- with destroy() as the
      LAST resort. One recovery attempt each, then mp4.

   THE LADDER, best first:
     native HLS -> hls.js over MSE -> the mp4 <source> already in the markup.
   hls.js (~90 KB gz, vendored) is fetched only on the middle rung, only once a
   video is near the viewport. Every homepage video is below the fold, so the
   LCP path never sees any of this. */

(function () {
  'use strict';

  var probe = document.createElement('video');
  var NATIVE_HLS = !!probe.canPlayType('application/vnd.apple.mpegurl');
  var MSE_OK = typeof window.MediaSource !== 'undefined' ||
               typeof window.WebKitMediaSource !== 'undefined';

  /* Identity-keyed state. WeakSets, not dataset: dataset survives
     cloneNode(true), and a clone is exactly the thing that must NOT inherit
     "already started". */
  var started = new WeakSet();
  var attached = new WeakSet();

  /* Live-instance cap. These are decorative loops; two visible at once is the
     realistic maximum, four is generous. Beyond that the oldest instance is
     destroyed and its element falls back to the (by now cached) mp4. */
  var MAX_HLS = 4;
  var live = [];   // [{video, hls}]

  function registerHls(video, hls) {
    live.push({ video: video, hls: hls });
    if (live.length > MAX_HLS) {
      var oldest = live.shift();
      try { oldest.hls.destroy(); } catch (e) {}
      fallToMp4(oldest.video);
    }
  }

  function unregisterHls(hls) {
    for (var i = 0; i < live.length; i++) {
      if (live[i].hls === hls) { live.splice(i, 1); return; }
    }
  }

  var hlsPromise = null;
  function loadHlsJs() {
    if (hlsPromise) return hlsPromise;
    hlsPromise = new Promise(function (resolve, reject) {
      if (window.Hls) { resolve(window.Hls); return; }
      var src = window.__everawHlsSrc;
      if (!src) { reject(new Error('no hls src')); return; }
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () { window.Hls ? resolve(window.Hls) : reject(new Error('no Hls global')); };
      s.onerror = function () { reject(new Error('hls.js failed to load')); };
      document.head.appendChild(s);
    });
    return hlsPromise;
  }

  /* Give the element back its mp4 <source>: removing the src attribute and
     calling load() makes the browser re-evaluate the children. Works whether
     the src held a manifest URL or a dead MediaSource blob. */
  function fallToMp4(video) {
    if (video.src) { video.removeAttribute('src'); }
    video.preload = 'auto';
    try { video.load(); } catch (e) {}
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  }

  function playPlain(video) {
    video.preload = 'auto';
    video.load();
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  }

  function hlsConfig() {
    return {
      capLevelToPlayerSize: true,
      /* See falsification #1: without this, DPR pushes every box we have onto
         the top rendition and the cap is a no-op. */
      ignoreDevicePixelRatio: true,
      startLevel: -1,
      /* These clips loop. A short forward buffer evicts the head before `loop`
         seeks to 0 and every lap re-fetches; generous buffer + infinite back
         buffer means one pass through the clip and every later lap is free.
         (backBufferLength Infinity is the library default; stated here because
         the looping is WHY the default must not be "tuned" down.) */
      maxBufferLength: 60,
      maxMaxBufferLength: 120,
      backBufferLength: Infinity
    };
  }

  /* Shared MSE path with the documented recovery ladder (falsification #5). */
  function attachHlsJs(Hls, video, manifest, onReady) {
    var hls = new Hls(hlsConfig());
    var recoveredMedia = false;
    var recoveredNet = false;
    var done = false;

    function bail() {
      if (done) return;
      done = true;
      unregisterHls(hls);
      try { hls.destroy(); } catch (e) {}
      fallToMp4(video);
      if (onReady) onReady();
    }

    hls.on(Hls.Events.MANIFEST_PARSED, function () {
      if (onReady && !done) onReady();
    });
    hls.on(Hls.Events.ERROR, function (_evt, data) {
      if (!data || !data.fatal) return;
      if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !recoveredMedia) {
        recoveredMedia = true;
        try { hls.recoverMediaError(); return; } catch (e) {}
      } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR && !recoveredNet) {
        recoveredNet = true;
        try { hls.startLoad(); return; } catch (e) {}
      }
      bail();
    });

    registerHls(video, hls);
    hls.loadSource(manifest);
    hls.attachMedia(video);
    return hls;
  }

  /* Native rung with an actual failure path (falsification #4). */
  function playNative(video, manifest) {
    var onErr = function () {
      video.removeEventListener('error', onErr);
      fallToMp4(video);
    };
    video.addEventListener('error', onErr);
    video.src = manifest;
    playPlain(video);
  }

  function begin(video) {
    if (started.has(video)) return;
    started.add(video);

    /* A cloned element can arrive carrying a dead blob: src from the original's
       MediaSource. Strip it so the ladder starts clean (falsification #3). */
    if (/^blob:/.test(video.src || '')) video.removeAttribute('src');

    var manifest = video.getAttribute('data-hls-src');
    if (!manifest) { playPlain(video); return; }
    if (NATIVE_HLS) { playNative(video, manifest); return; }
    if (!MSE_OK) { playPlain(video); return; }

    loadHlsJs().then(function (Hls) {
      if (!Hls || !Hls.isSupported()) { playPlain(video); return; }
      attachHlsJs(Hls, video, manifest, function () {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      });
    }).catch(function () { playPlain(video); });
  }

  /* Source-only entry point for callers that own their own play() -- the hero,
     which manages Splide clones and pauses off-screen slides itself. */
  function attach(video, onReady) {
    function ready() { if (typeof onReady === 'function') onReady(video); }

    if (attached.has(video)) { ready(); return; }
    attached.add(video);

    if (/^blob:/.test(video.src || '')) video.removeAttribute('src');

    var manifest = video.getAttribute('data-hls-src');
    if (!manifest) { ready(); return; }
    if (NATIVE_HLS) {
      var onErr = function () {
        video.removeEventListener('error', onErr);
        fallToMp4(video);
      };
      video.addEventListener('error', onErr);
      video.src = manifest;
      ready();
      return;
    }
    if (!MSE_OK) { ready(); return; }

    loadHlsJs().then(function (Hls) {
      if (!Hls || !Hls.isSupported()) { ready(); return; }
      attachHlsJs(Hls, video, manifest, ready);
    }).catch(ready);
  }

  function lazyPlay(selector, rootMargin) {
    var videos = document.querySelectorAll(selector);
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(videos, begin);
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          begin(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: rootMargin || '200px' });
    Array.prototype.forEach.call(videos, function (v) { observer.observe(v); });
  }

  window.EverawVideo = {
    begin: begin,
    attach: attach,
    lazyPlay: lazyPlay,
    nativeHLS: NATIVE_HLS,
    canAdapt: NATIVE_HLS || MSE_OK
  };
}());
